import { useState, useEffect } from 'react';
import axios from 'axios';

export const useNotificationSettings = (getToken) => {
    const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(false);
    const [pushNotificationsEnabled, setPushNotificationsEnabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notificationsSettings, setNotificationsSettings] = useState(null);
    const [originalPushNotificationValue, setOriginalPushNotificationValue] = useState(null);

    const showMessage = (message, type = 'error') => {
        // Simple console log for now - could be enhanced with toast notifications
        console.log(`${type.toUpperCase()}: ${message}`);
    };

    const loadNotificationsSettings = () => {
        setLoading(true);
        setError(null);
        getToken()
        .then((token) => {
            axios.get("https://firewatch-api.space-eyes.com/user/notification-settings", {
                headers: {
                  'Authorization': `Bearer ${token}`
                },
                timeout: 10000 
              })
              .then((response) => {
                console.log('notifications settings loaded: ', response.data);
                const settings = response.data.notification[0];
                setNotificationsSettings(settings);
                setEmailNotificationsEnabled(settings.email);
                setPushNotificationsEnabled(settings.app_notification);
                setOriginalPushNotificationValue(settings.app_notification); // Store the original value
                setLoading(false);
              })
              .catch((error) => {
                console.log('error getting notifications settings: ', error);
                let errorMessage = 'Failed to load notification settings. Please try again.';
                
                if (error.code === 'ECONNABORTED') {
                    errorMessage = 'Request timed out. Please check your internet connection and try again.';
                } else if (error.message === 'Network Error' || !error.response) {
                    errorMessage = 'No internet connection. Please check your network and try again.';
                } else if (error.response?.status === 401) {
                    errorMessage = 'Authentication failed. Please sign in again.';
                } else if (error.response?.status >= 500) {
                    errorMessage = 'Server error. Please try again later.';
                }
                
                setError(errorMessage);
                setLoading(false);
                showMessage(errorMessage, 'error');
              });
        })
        .catch((error) => {
            console.log('error getting token: ', error);
            let errorMessage = 'Failed to authenticate. Please try again.';
            
            if (error.message === 'Network Error' || !error.response) {
                errorMessage = 'No internet connection. Please check your network and try again.';
            }
            
            setError(errorMessage);
            setLoading(false);
            showMessage(errorMessage, 'error');
        });
    };

    const updateNotificationsSettings = (emailValue) => {
        getToken().then((token) => {
            axios.post("https://firewatch-api.space-eyes.com/user/notification-settings", {
                email: emailValue,
                app_notification: originalPushNotificationValue // Always use the original value from server
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                timeout: 10000 
            })
            .then((response) => {
                console.log('notifications settings updated: ', response.data);
                showMessage('Email notification settings updated successfully', 'success');
            })
            .catch((error) => {
                console.log('error changing notifications: ', error);
                let errorMessage = 'Failed to update notification settings. Please try again.';
                
                if (error.code === 'ECONNABORTED') {
                    errorMessage = 'Request timed out. Please check your internet connection and try again.';
                } else if (error.message === 'Network Error' || !error.response) {
                    errorMessage = 'No internet connection. Please check your network and try again.';
                } else if (error.response?.status === 401) {
                    errorMessage = 'Authentication failed. Please sign in again.';
                } else if (error.response?.status >= 500) {
                    errorMessage = 'Server error. Please try again later.';
                }
                
                showMessage(errorMessage, 'error');
                
                // Revert only the email notification state on error
                setEmailNotificationsEnabled(!emailValue);
            });
        }).catch((error) => {
            console.log('error getting token: ', error);
            let errorMessage = 'Failed to authenticate. Please try again.';
            
            if (error.message === 'Network Error' || !error.response) {
                errorMessage = 'No internet connection. Please check your network and try again.';
            }
            
            showMessage(errorMessage, 'error');
            
            // Revert only the email notification state on error
            setEmailNotificationsEnabled(!emailValue);
        });
    };

    const handleEmailNotificationsToggle = (value) => {
        setEmailNotificationsEnabled(value);
        updateNotificationsSettings(value);
    };

    // Remove push notifications toggle since we're not using it in the web UI
    // const handlePushNotificationsToggle = (value) => {
    //     setPushNotificationsEnabled(value);
    //     updateNotificationsSettings(emailNotificationsEnabled, value);
    // };

    useEffect(() => {
        loadNotificationsSettings();
    }, []);

    return {
        emailNotificationsEnabled,
        pushNotificationsEnabled, // Still available for reading, but not for toggling
        loading,
        error,
        notificationsSettings,
        handleEmailNotificationsToggle,
        // handlePushNotificationsToggle, // Removed since we're not using it
        loadNotificationsSettings
    };
};
